import { useCallback, useEffect, useState } from "react";

import { PasskeyRegistration, securityApi, securityErrorCode, securityErrorStatus, TotpEnrollment } from "../../backend/security";
import { useCountdown } from "../../utils/countdown";
import { getUser } from "../../utils/local-storage";
import { rememberPasskey } from "../../utils/passkey-hint";
import { createPasskeyCredential, guessPasskeyName, isPasskeySupported } from "../../utils/webauthn";
import { useReauthGate } from "./useReauthGate";

export type EnrollmentMethod = "totp" | "passkey";

export const RECOVERY_COPY = "Keep a second way in. If you lose this device you'll need another sign-in method to get back into projects that require two-step verification.";

/**
 * Adding an authenticator app or a passkey, shared by the classic and the new UI's dialogs.
 * Steps: 0 scan (or name the passkey), 1 verify (or register), 2 done.
 */
export function useSecurityEnrollment({ open, initialMethod, onComplete }: {
    open: boolean;
    initialMethod?: EnrollmentMethod;
    onComplete: (factorType: EnrollmentMethod) => void;
}) {
    const [method, setMethod] = useState<EnrollmentMethod | undefined>(initialMethod);
    const [step, setStep] = useState(0);

    const [totpEnrollment, setTotpEnrollment] = useState<TotpEnrollment>();
    const [starting, setStarting] = useState(false);
    const [startFailed, setStartFailed] = useState(false);
    const [restarted, setRestarted] = useState(false);
    const [code, setCode] = useState("");

    const [passkeyName, setPasskeyName] = useState("");
    const [registering, setRegistering] = useState(false);

    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState<string>();
    const reauth = useReauthGate();

    const totpCountdown = useCountdown(totpEnrollment?.expiresAt);

    const reset = useCallback(() => {
        setStep(0);
        setTotpEnrollment(undefined);
        setStartFailed(false);
        setRestarted(false);
        setCode("");
        setError(undefined);
        setRegistering(false);
        setVerifying(false);
        setPasskeyName(guessPasskeyName());
    }, []);

    useEffect(() => {
        if (open) {
            setMethod(initialMethod);
            reset();
        }
    }, [initialMethod, open, reset]);

    const startTotp = useCallback(async (isRestart = false) => {
        try {
            setStarting(true);
            setStartFailed(false);
            setError(undefined);
            const enrollment = await securityApi.startTotpEnrollment();
            setTotpEnrollment(enrollment);
            setCode("");
            setRestarted(isRestart);
            setStep(0);
        } catch (error: any) {
            if (securityErrorCode(error) === "REAUTHENTICATION_REQUIRED") {
                reauth.require(() => startTotp(isRestart));
                return;
            }
            setStartFailed(true);
        } finally {
            setStarting(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (open && method === "totp" && !totpEnrollment && !starting && !startFailed && !reauth.reauthenticating) {
            startTotp();
        }
    }, [method, open, reauth.reauthenticating, startFailed, starting, startTotp, totpEnrollment]);

    const verifyTotp = async (submitted: string = code) => {
        if (!totpEnrollment || submitted.length !== 6) {
            return;
        }

        try {
            setVerifying(true);
            setError(undefined);
            setRestarted(false);
            await securityApi.verifyTotpEnrollment(totpEnrollment.transactionId, submitted);
            setStep(2);
            onComplete("totp");
        } catch (error: any) {
            const errorCode = securityErrorCode(error);
            setCode("");

            if (errorCode === "REAUTHENTICATION_REQUIRED") {
                // The field was cleared above; retry with the code that was submitted.
                reauth.require(() => verifyTotp(submitted));
                return;
            }
            if (securityErrorStatus(error) === 410 || errorCode === "TRANSACTION_EXPIRED") {
                await startTotp(true);
                return;
            }
            setError(error?.response?.data?.message || "The code was not accepted. Try the current code.");
        } finally {
            setVerifying(false);
        }
    };

    const registerPasskey = async () => {
        if (!isPasskeySupported()) {
            setError("This browser doesn't support passkeys.");
            return;
        }

        let registration: PasskeyRegistration | undefined;
        try {
            setRegistering(true);
            setError(undefined);
            registration = await securityApi.startPasskeyRegistration();
        } catch (error: any) {
            setRegistering(false);
            if (securityErrorCode(error) === "REAUTHENTICATION_REQUIRED") {
                reauth.require(registerPasskey);
                return;
            }
            setError(error?.response?.data?.message || "We couldn't start passkey setup. Please try again.");
            return;
        }

        try {
            const credential = await createPasskeyCredential(registration.publicKey);
            await securityApi.verifyPasskeyRegistration(registration.transactionId, credential, passkeyName.trim() || undefined);
            // Made in this browser, so the next sign-in here can open it straight after the email
            rememberPasskey(getUser()?.email);
            setStep(2);
            onComplete("passkey");
        } catch (error: any) {
            if (error?.name === "NotAllowedError") {
                // The user dismissed the browser prompt.
                return;
            }
            const errorCode = securityErrorCode(error);
            if (errorCode === "REAUTHENTICATION_REQUIRED") {
                reauth.require(registerPasskey);
                return;
            }
            if (securityErrorStatus(error) === 410 || errorCode === "TRANSACTION_EXPIRED") {
                setError("That passkey request expired. Try again.");
                return;
            }
            if (error?.name === "SecurityError") {
                // Passkeys belong to one address (the relying party), and this page isn't on it,
                // e.g. localhost against a deployed environment
                setError("Passkeys can't be added from this address. Add one on the Microtica site you normally sign in to.");
                return;
            }
            if (error?.name === "InvalidStateError") {
                setError("This device already has a passkey for your account.");
                return;
            }
            setError(error?.response?.data?.message || "We couldn't add this passkey. Please try again.");
        } finally {
            setRegistering(false);
        }
    };

    /** Starts over with another method, e.g. "Add a passkey too" after the app */
    const choose = (next: EnrollmentMethod) => {
        setMethod(next);
        reset();
    };

    return {
        method, choose, step, setStep,
        totpEnrollment, starting, startFailed, restarted, totpCountdown, code, setCode, verifying, startTotp, verifyTotp,
        passkeyName, setPasskeyName, registering, registerPasskey,
        error, setError, reauth,
    };
}
